<template>
  <div class="w-full h-full pointer-events-none">
    <ClientOnly>
      <Portal :disabled="!renderToPortal" to="annotations-canvas">
        <div class="absolute inset-0 z-20 w-full h-full">
          <section
            ref="container"
            class="annotations w-full h-full"
            :style="{ cursor: disabled ? 'not-allowed' : 'auto', pointerEvents: active ? 'auto' : 'none' }"
          >
            <canvas ref="canvas" class="block" draggable="false">Your browser does not support HTML canvas.</canvas>
          </section>
        </div>
      </Portal>
      <Portal v-slot="overlayProps" to="deck-controls-annotations">
        <DeckAnnotationsControl
          class="pointer-events-auto"
          :user="user"
          :stroke="stroke"
          :active="active"
          :tooltip-direction="overlayProps.tooltipDirection"
          @reset="reset"
          @undo="undo"
          @toggle-active="$emit('toggle-active')"
          @update:stroke="$emit('update', { stroke: $event })"
        />
      </Portal>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { AnnotationColors } from '@/lib/annotations/constants'
import type { DeckUser } from '@/entities/deck'
import type { TooltipDirection } from '~/components/VTooltip.vue'
import type { DeepReadonly } from 'vue'

const props = defineProps({
  active: {
    type: Boolean,
    required: true,
  },
  stroke: {
    type: Number as PropType<AnnotationColors>,
    default: AnnotationColors.RED,
  },
  lineWidth: {
    type: Number,
    default: 3,
  },
  aspect: {
    type: Number,
    default: 4 / 3,
  },
  zoom: {
    type: Number,
    default: 1,
  },
  pan: {
    type: Object as PropType<Point>,
    default: () => ({ x: 0, y: 0 }),
  },
  uvCoords: {
    type: Object as PropType<Point | null>,
    default: null,
  },
  objectFit: {
    type: String as PropType<'cover' | 'contain'>,
    default: 'contain',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  user: {
    type: Object as PropType<DeepReadonly<DeckUser>>,
    default: () => ({}),
  },
  tooltipDirection: {
    type: String as PropType<TooltipDirection>,
    default: 'right',
  },
  renderToPortal: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['annotate', 'toggle-active', 'update', 'subscribe'])

const { $analytics } = useNuxtApp()

const canvas = ref<HTMLCanvasElement | undefined>()
const container = ref<HTMLElement | undefined>()

const { undo, reset } = useAnnotations({
  canvas,
  container,
  annotate: drawSpec => emit('annotate', drawSpec),
  update: payload => emit('update', payload),
  subscribe: subscriber => emit('subscribe', subscriber),
  ...toRefs(props),
})

watch(
  [canvas, container],
  ([canvas, container], [_, oldContainer]) => {
    if (!canvas || !container) {
      oldContainer?.removeEventListener('mouseup', _trackMouseUp)
      return
    }
    container.addEventListener('mouseup', _trackMouseUp)
  },
  { immediate: true }
)

onUnmounted(() => {
  container.value?.removeEventListener('mouseup', _trackMouseUp)
})

function _trackMouseUp() {
  $analytics.track('annotationsLineEnded', { isHost: props.user.deckUserType === 'host' })
}
</script>
